<template>
  <div class="container-fluid">
    <page-header
      :page="page"
      has-return
      left-side-class="col-xl-8"
      right-side-class="col-xl-4 d-flex align-items-end justify-content-end pb-2"
    >
      <action-button
        v-if="readonly && !isSubmitted"
        class="m-1"
        text="Someter"
        i="fas fa-flag"
        variant="success m-1"
        @click="submit"
      />
      <action-button
        v-else-if="!readonly"
        class="m-1"
        text="Continuar"
        i="fa fa-arrow-alt-circle-right"
        variant="success m-1"
        @click="onContinue"
      />
    </page-header>
    <div class="add-requisition">
      <section-title
        icon="fa fa-list-ul"
        content="Información de Requisición"
        class="add-requisition__section-title"
        title-class="add-requisition__section-title__info"
        container-class="add-requisition__section-title__container"
      />

      <template v-if="readonly">
        <requisition-info :is-submitted="isSubmitted" :info="requisition" />
        <validation-observer ref="selectedArticlesData">
          <selected-items
            v-model="requisition.articles"
            :requisition-id="requestId"
            :is-submitted="isSubmitted"
            readonly
          />
        </validation-observer>
      </template>

      <template v-else>
        <validation-observer ref="generalForm">
          <requisition-form @formDataChanged="formDataChanged" />
        </validation-observer>

        <detail
          @updateSelectedRequest="updateSelectedRequest"
          @updateRequestResultCount="updateRequestResultCount"
          @display-alert="displayAlertHandler"
        >
          <selected-items
            v-model="selectedRequest"
            @on-remove-parent="onRemoveParentItemHandler"
            @on-remove-child="onRemoveChildItemHandler"
          />
        </detail>
        <error
          v-if="errors.searchRequest && searchError"
          variant="no-border"
          message="Favor realizar la búsqueda de un artículo."
        />
      </template>
    </div>
  </div>
</template>
<script>
import PageHeader from 'PageHeader';
import ActionButton from 'ActionButton';
import SectionTitle from 'SectionTitle';
import Error from 'custom-error';
import RequisitionForm from './RequisitionForm.vue';
import RequisitionInfo from './RequisitionInfo.vue';
import SelectedItems from './SelectedItems.vue';
import Detail from './Detail.vue';
import alert from '@/mixin/alert.mixin';
import {
  saveRequisition,
  getRequisitionDetail,
  submitRequisition,
} from '@/modules/materials-equipment/api.js';
import requisitionStatus from '@/utils/constants/pei/materials-equipment-requisition/meRequesitionStatus.js';

export default {
  name: 'AddMaterialsEquipmentRequisition',
  mixins: [alert],
  components: {
    PageHeader,
    ActionButton,
    SectionTitle,
    Error,
    RequisitionForm,
    Detail,
    RequisitionInfo,
    SelectedItems,
  },
  data() {
    return {
      page: {
        icon: 'fas fa-user-edit',
        title: 'Manejo de Requisición de Materiales y Equipos',
        routes: [
          'Inicio',
          'PEI',
          'Manejo de Requisición de Materiales y Equipos',
        ],
      },
      formData: null,
      requestResultCount: 0,
      selectedRequest: [],
      requisition: {},
      errors: {
        searchRequest: false,
      },
      requestId: null,
    };
  },
  computed: {
    readonly() {
      return Object.values(requisitionStatus).includes(
        this.requisition.requisitionStatus?.id
      );
    },
    searchError() {
      return this.requestResultCount == 0;
    },
    isSubmitted() {
      return (
        this.requisition.requisitionStatus?.id == requisitionStatus.Submitted
      );
    },
  },
  async mounted() {
    await this.getData();
  },
  methods: {
    async getData() {
      this.requestId = Number(this.$route.params.requestId) || null;
      if (this.requestId == null) return;

      const { data } = await getRequisitionDetail(this.requestId);
      this.requisition = { ...data };
    },
    formDataChanged(newValue) {
      this.formData = newValue;
    },
    async onContinue() {
      const isGeneralFormValid = await this.$refs.generalForm.validate();

      if (!isGeneralFormValid || this.searchError) {
        this.errors.searchRequest = this.searchError;
        return this.displayAlertHandler(
          'Favor de completar la información solicitada para continuar.'
        );
      }

      if (this.selectedRequest.length == 0) {
        return this.displayAlertHandler(
          'Favor seleccionar al menos una solicitud.'
        );
      }
      if (this.selectedRequest.length > 25) {
        return this.displayAlertHandler(
          'No es posible continuar, ya la cantidad artículos se excede de 25.'
        );
      }
      const detail = [];
      this.selectedRequest.forEach((r) => {
        detail.push(
          ...r.articles.map((a) => ({
            itemTypeId: r.id,
            quantityApproved: a.quantityApproved,
            requestDetailId: a.id,
            price: parseFloat(r.price),
          }))
        );
      });
      const payload = {
        detail,
        requisitionTypeId: this.formData.requisitionType.id,
        supplierName: this.formData.supplierName,
        contractNumber: this.formData.contractNumber,
        description: this.formData.description,
      };
      await saveRequisition(payload).then(async ({ data }) => {
        await this.$router.replace({
          name: 'materialAddRequisition',
          params: { requestId: data },
        });
        await this.getData();
        this.displayAlertHandler(
          'La requisición se ha creado exitosamente.',
          true
        );
      });
    },
    async submit() {
      const valid = await this.$refs.selectedArticlesData.validate();
      if (!valid) {
        return this.displayAlertHandler(
          'Favor de completar la información solicitada para continuar.'
        );
      }

      const params = this.requisition.articles.map((a) => ({
        articleId: a.id,
        measure: a.measure?.id,
        purchaseOrderId: a.purchaseOrder?.id,
      }));

      await submitRequisition(this.requestId, params).then(async () => {
        await this.getData();
        this.displayAlertHandler(
          'La requisición ha sido sometida exitosamente.',
          true
        );
      });
    },
    updateSelectedRequest(newValue) {
      this.selectedRequest = newValue;
    },
    updateRequestResultCount(newValue) {
      this.requestResultCount = newValue;
    },
    displayAlertHandler(msg, isSuccess) {
      this.ShowToast(
        isSuccess ? 'Proceso exitoso' : 'Alerta',
        msg,
        isSuccess ? 'success' : 'error'
      );
    },
    onRemoveParentItemHandler(index) {
      this.selectedRequest.splice(index, 1);
    },
    onRemoveChildItemHandler(parentIndex, childIndex) {
      this.selectedRequest[parentIndex].articles.splice(childIndex, 1);

      //if there are no more items then remove parent
      if (this.selectedRequest[parentIndex].articles.length === 0) {
        this.selectedRequest.splice(parentIndex, 1);
      }
    },
  },
};
</script>
<style lang="scss" scoped>
@import '@/assets/scss/variables/_colors.scss';

::v-deep .add-requisition {
  &__section-title {
    &__info {
      margin-top: 0 !important;
    }
    &__container {
      background: map-get($colors, 'orange-light');
      display: inline-flex;
      align-items: center;
      padding: 0.1rem 1.2rem 0.1rem 0.1rem !important;
    }
  }
}
</style>
